import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { motion } from "framer-motion";
import { Instagram, ArrowRight, Play, Images } from "lucide-react";
import { getInstagramFeed } from "@/lib/instagram.functions";


const PROFILE_URL = "https://www.instagram.com/kentrosistemas/";

function formatDate(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

export function InstagramFeed() {
  const fetchFeed = useServerFn(getInstagramFeed);
  const { data, isLoading } = useQuery({
    queryKey: ["instagram-feed"],
    queryFn: () => fetchFeed(),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const posts = data?.posts ?? [];

  return (
    <section className="bg-secondary/30 py-20" aria-labelledby="instagram-feed-title">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              <Instagram className="size-3.5" /> Tempo real
            </span>
            <h2
              id="instagram-feed-title"
              className="mt-4 text-3xl font-bold tracking-tight md:text-4xl"
            >
              A Kentro <span className="gradient-text">por dentro</span>
            </h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              As 8 últimas publicações do nosso Instagram — bastidores do time, novidades do produto
              e conteúdo sobre atendimento com IA.
            </p>
          </div>
          <a
            href={PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={"Seguir a Kentro no Instagram"}
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-5 py-3 text-sm font-semibold text-foreground transition hover:bg-primary/20"
          >
            <Instagram className="size-4" /> Seguir no Instagram
            <ArrowRight className="size-4" />
          </a>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {isLoading && posts.length === 0
            ? Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square animate-pulse rounded-2xl border border-border/60 bg-muted/50"
                  aria-hidden
                />
              ))
            : posts.map((post, i) => (
                <motion.a
                  key={post.id}
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: Math.min(i, 7) * 0.04 }}
                  className="group relative block aspect-square overflow-hidden rounded-2xl border border-border/60 bg-card"
                >
                  <img
                    src={post.imageUrl}
                    alt={
                      post.caption
                        ? `Publicação da Kentro no Instagram: ${post.caption.slice(0, 90)}`
                        : "Publicação da Kentro no Instagram"
                    }
                    loading="lazy"
                    decoding="async"
                    className="size-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  {post.mediaType !== "IMAGE" && (
                    <span className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white">
                      {post.mediaType === "VIDEO" ? (
                        <Play className="size-3.5" />
                      ) : (
                        <Images className="size-3.5" />
                      )}
                    </span>
                  )}
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8 text-left">
                    <span className="line-clamp-2 text-xs font-medium text-white/90">
                      {post.caption || "Ver no Instagram"}
                    </span>
                    <span className="mt-1 block text-[11px] text-white/60">
                      {formatDate(post.timestamp)}
                    </span>
                  </span>
                </motion.a>
              ))}
        </div>

        {!isLoading && posts.length === 0 && (
          <p className="mt-8 text-center text-sm text-muted-foreground">
            Não foi possível carregar o feed agora.{" "}
            <a
              href={PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Ver publicações no Instagram
            </a>
            .
          </p>
        )}
      </div>
    </section>
  );
}

export default InstagramFeed;
